import {
  Button,
  Divider,
  CircularProgress,
  Paper,
  TableContainer,
  Switch,
  Table,
  TableCell,
  TableHead,
  TableRow,
  Typography,
  Autocomplete,
  TextField,
  IconButton,
  Tooltip,
  TablePagination,
} from "@mui/material";
import { assetsUrl } from "../../../constants";
import { ChangeEvent, SyntheticEvent, useRef, useState } from "react";
import { useMutation, useQuery } from "react-query";
import {
  deleteRateData,
  getMasterTableData,
  updateRateStatus,
  uploadRateMasterFile,
} from "./services";
import usePopup from "../../../hooks/usePopup";
import CustomTableBody from "../../../utilis/component/CustomTableBody";
import { MasterTableDataType, SearchParamsType } from "./types";
import debounce from "../../../utilis/methods/networkHandling";
import DeleteIcon from "@mui/icons-material/Delete";
const pageTitle = "Rate";
const RateMaster = () => {
  const { showSnackbar } = usePopup();
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [searchParams, setSearchParams] = useState<SearchParamsType>({
    pageNumber: 0,
    pageSize: 25,
    searchText: "",
    sortField: "",
    sortOrder: "",
    status: 2,
  });
  const fileRef = useRef<HTMLInputElement | null>(null);
  const {
    data: tableData,
    isLoading: tableDataLoading,
    refetch,
  } = useQuery(
    ["rateMastertableData", searchParams],
    async () => await getMasterTableData(searchParams),
    {
      refetchOnWindowFocus: false,
      enabled: true,
    }
  );

  const { mutate: fileUpload, isLoading: uploadLoading } = useMutation(
    uploadRateMasterFile,
    {
      onSuccess: (result) => {
        if (result.responseIndicator == "success") {
          fileRef.current!.value = "";
          refetch();
          showSnackbar({
            message: result.responseMessage,
            open: true,
            variant: "success",
            duration: 3000,
          });
        } else {
          showSnackbar({
            message: result.responseMessage,
            open: true,
            variant: "warning",
            duration: 3000,
          });
        }
      },
      onError: (e: any) => {
        showSnackbar({
          message:
            e?.response?.data?.responseMessage || "Something went wrong !",
          open: true,
          variant: "error",
          duration: 3000,
        });
      },
    }
  );
  const { mutate: updateStatus, isLoading: updateLoading } = useMutation(
    updateRateStatus,
    {
      onSuccess: (result) => {
        if (result.responseIndicator == "success") {
          refetch();
          showSnackbar({
            message: result.responseMessage,
            open: true,
            variant: "success",
            duration: 3000,
          });
        } else {
          showSnackbar({
            message: result.responseMessage,
            open: true,
            variant: "warning",
            duration: 3000,
          });
        }
      },
      onError: (e: any) => {
        showSnackbar({
          message:
            e?.response?.data?.responseMessage || "Something went wrong !",
          open: true,
          variant: "error",
          duration: 3000,
        });
      },
    }
  );
  const { mutate: deleteFile, isLoading: deleLoading } = useMutation(
    deleteRateData,
    {
      onSuccess: (result) => {
        if (result.responseIndicator == "success") {
          refetch();
          showSnackbar({
            message: result.responseMessage,
            open: true,
            variant: "success",
            duration: 3000,
          });
        } else {
          showSnackbar({
            message: result.responseMessage,
            open: true,
            variant: "warning",
            duration: 3000,
          });
        }
      },
      onError: (e: any) => {
        showSnackbar({
          message:
            e?.response?.data?.responseMessage || "Something went wrong !",
          open: true,
          variant: "error",
          duration: 3000,
        });
      },
    }
  );

  const handleUploadFile = () => {
    if (fileRef.current?.files && fileRef.current.files.length > 0) {
      fileUpload(fileRef.current.files[0]);
    } else {
      showSnackbar({
        message: "Please select a file to upload !",
        open: true,
        variant: "warning",
        duration: 3000,
      });
    }
  };

  const handleUpdateOneStatus = (
    status: boolean,
    data: MasterTableDataType
  ) => {
    setSelectedId(data.priceListId);
    updateStatus({
      priceListId: data.priceListId,
      status: status ? 1 : 0,
    });
  };

  const handleDeleteOne = (data: MasterTableDataType) => {
    setSelectedId(data.priceListId);
    deleteFile(data.priceListId);
  };

  const handleStatusChange = (
    _: SyntheticEvent,
    value: { label: string; value: number } | null
  ) => {
    setSearchParams((prev) => ({
      ...prev,
      status: value ? value.value : 3,
    }));
  };

  const handleSearchChange = (e: ChangeEvent<HTMLInputElement>) => {
    setSearchParams((prev) => ({
      ...prev,
      searchText: e.target.value,
    }));
  };

  const debounceSearch = debounce(handleSearchChange, 500);

  const handleChangePage = (
    _: React.MouseEvent<HTMLButtonElement> | null,
    pageNumber: number
  ) => {
    setSearchParams((prev) => ({
      ...prev,
      pageNumber,
    }));
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    setSearchParams((prev) => ({
      ...prev,
      pageNumber: 0,
      pageSize: parseInt(event.target.value, 10),
    }));
  };

  return (
    // <></>
    <div className="p-3">
      <Paper>
        <div className="p-3">
          <Typography variant="h6">{pageTitle} Master</Typography>
        </div>
        <Divider />
        <div className="flex justify-between items-center">
          <div className="p-4 flex justify-center">
            <input ref={fileRef} type="file" accept=".xls,.xlsx" />
            <Button
              variant="text"
              component="a"
              href={`${assetsUrl}/masters-sample-files/sample-ratemaster.xlsx`}
              download={true}
            >
              Download sample
            </Button>
          </div>
          <Divider />
          <div className=" p-3 flex justify-end items-center">
            <Button
              disabled={uploadLoading}
              onClick={handleUploadFile}
              endIcon={uploadLoading && <CircularProgress size={20} />}
            >
              upload
            </Button>
          </div>
        </div>
        <Divider />
        <div className="p-2 grid grid-cols-4 gap-2">
          <Autocomplete
            disableClearable
            fullWidth
            defaultValue={
              selectedStatus[searchParams.status as keyof typeof selectedStatus]
            }
            options={statusOptions}
            getOptionLabel={(option) => option.label}
            isOptionEqualToValue={(option, value) =>
              option.value === value.value
            }
            renderInput={(params) => <TextField {...params} label="Status" />}
            onChange={handleStatusChange}
          />
          <TextField label={"Search"} onChange={debounceSearch} />
        </div>
        <TableContainer>
          <Table stickyHeader size="small">
            <TableHead>
              <TableRow>
                {headings.map((heading) => (
                  <TableCell key={heading.key} className="font-bold">
                    {heading.label}
                  </TableCell>
                ))}
                <TableCell className="font-bold">Status</TableCell>
                <TableCell className="font-bold">Actions</TableCell>
              </TableRow>
            </TableHead>
            <CustomTableBody
              alert={"No data"}
              dataLoading={tableDataLoading}
              isData={Boolean(tableData?.response?.items?.length)}
              totalSpan={{
                row: searchParams.pageSize,
                col: headings.length + 2,
              }}
            >
              {tableData?.response?.items?.map((tableItem, index) => (
                <TableRow key={`${pageTitle}-${index}`}>
                  <TableCell>{tableItem.priceListCptCode}</TableCell>
                  <TableCell>{tableItem.priceListBaseRate}</TableCell>
                  <TableCell>{tableItem.priceListMinistryName}</TableCell>
                  <TableCell>
                    {tableItem.status == 1 ? (
                      <div className="text-green-600 text-center rounded-[10px] w-[70px] font-semibold">
                        Active
                      </div>
                    ) : (
                      <div className="text-red-600 text-center rounded-[10px] w-[70px] font-semibold">
                        Inactive
                      </div>
                    )}
                  </TableCell>
                  <TableCell>
                    <div className="flex gap-2">
                      <div className="flex justify-center items-center gap-2">
                        {updateLoading &&
                        tableItem.priceListId == selectedId ? (
                          <CircularProgress size={18} />
                        ) : (
                          <Switch
                            checked={Boolean(tableItem.status)}
                            disabled={
                              updateLoading &&
                              tableItem.priceListId == selectedId
                            }
                            onChange={(e) => {
                              handleUpdateOneStatus(
                                e.target.checked,
                                tableItem
                              );
                            }}
                          />
                        )}

                        <Tooltip title="Delete">
                          <IconButton
                            color="error"
                            onClick={() => {
                              handleDeleteOne(tableItem);
                            }}
                          >
                            {deleLoading &&
                            tableItem.priceListId == selectedId ? (
                              <CircularProgress color="error" size={18} />
                            ) : (
                              <DeleteIcon />
                            )}
                          </IconButton>
                        </Tooltip>
                      </div>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </CustomTableBody>
          </Table>
          <div className="flex justify-end">
            <TablePagination
              rowsPerPageOptions={[5, 10, 25]}
              component={"div"}
              count={tableData?.response?.totalRecords || 0}
              rowsPerPage={searchParams.pageSize}
              page={searchParams.pageNumber}
              sx={{ border: 0 }}
              slotProps={{
                select: {
                  inputProps: {
                    "aria-label": "rows per page",
                  },
                  native: true,
                },
              }}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              // ActionsComponent={TablePaginationActions}
            />
          </div>
        </TableContainer>
      </Paper>
    </div>
  );
};

export default RateMaster;
const headings = [
  {
    label: "CPT CODE",
    key: "icdCode",
  },
  {
    label: "BASE RATE",
    key: "icdName",
  },
  {
    label: "MINISTRY NAME",
    key: "icdVersion",
  },
];
const statusOptions = [
  { label: "All", value: 2 },
  { label: "Active", value: 1 },
  { label: "Inactive", value: 0 },
];
// const status = [
//   { label: "Active", value: 1 },
//   { label: "Inactive", value: 0 },
// ];
const selectedStatus = {
  2: { label: "All", value: 2 },
  1: { label: "Active", value: 1 },
  0: { label: "Inactive", value: 0 },
};
